/**
 * Scoring primitives (M4.T6: body only).
 *
 * Formulas per PRD §3.7. Both functions return non-negative integers.
 *
 *   audience_pts = round(1000 * (1 - 0.5 * (order - 1) / max(1, present - 1)))
 *   drawer_pts   = num_correct_guessers >= 1
 *                    ? round(mult * (250 + 250 * num_correct_guessers))
 *                    : 0
 *
 * Difficulty multipliers (PRD §3.7): easy 1.0 / medium 1.3 / hard 1.7.
 *
 * This module is the canonical source for `DIFFICULTY_MULTIPLIER`. The
 * reducer's bootstrap (`reducer.ts#initialGameState`) sets a placeholder
 * triple inline today; the M4 reducer-wiring pass will re-export from here.
 *
 * Zero-guess gate (owner decision, audit 2026-06-11): the literal PRD v2
 * formula paid `round(mult * 250)` base even when NOBODY guessed, which
 * rewarded unguessable scribbles, the drawer's incentive is supposed to be
 * guessability (PRD §4). Drawer points are now gated on at least one
 * correct guess; zero guessers → 0. PRD §3.8's drawer-disconnect abort is
 * still routed to 0 by the reducer directly, not this function.
 */

import type { WordTier } from "./types.js";

/**
 * Tier multipliers applied to the drawer's per-turn formula.
 * Locked by PRD §3.7.
 */
export const DIFFICULTY_MULTIPLIER: Readonly<Record<WordTier, number>> = {
  easy: 1.0,
  medium: 1.3,
  hard: 1.7,
};

/** Maximum audience points (1st correct guess). */
const AUDIENCE_MAX = 1000;
/** Decay fraction: last correct guesser still gets (1 - DECAY) * MAX. */
const AUDIENCE_DECAY = 0.5;

/**
 * Audience points for a single correct guess, per PRD §3.7.
 *
 *   audience_pts = round(1000 * (1 - 0.5 * (order - 1) / max(1, present - 1)))
 *
 * First correct guesser (order=1) → 1000.
 * Last correct guesser (order=present_audience, when all guess) → 500.
 * Solo audience (present=1) → 1000 regardless of order.
 *
 * @param guessOrder       1-indexed position among correct guessers this turn.
 *                         Clamped: <1 → 1; >presentAudience → presentAudience.
 * @param presentAudience  Audience size at turn start (excludes the drawer).
 *                         Clamped: <1 → 1.
 *
 * @returns Integer in [500, 1000]. Uses `Math.round` (half-away-from-zero
 *          for positive values in JS).
 */
export function audiencePointsForGuess(guessOrder: number, presentAudience: number): number {
  // NaN → 0 (defaults to "first" semantics after clamp); +Infinity →
  // MAX_SAFE_INTEGER ("last" semantics after clamp); -Infinity → 0.
  const sanitize = (v: number): number => {
    if (Number.isNaN(v)) return 0;
    if (!Number.isFinite(v)) return v > 0 ? Number.MAX_SAFE_INTEGER : 0;
    return Math.floor(v);
  };
  const present = Math.max(1, sanitize(presentAudience));
  const order = Math.max(1, Math.min(present, sanitize(guessOrder)));
  const denom = Math.max(1, present - 1);
  const fraction = (order - 1) / denom;
  return Math.round(AUDIENCE_MAX * (1 - AUDIENCE_DECAY * fraction));
}

/**
 * Drawer points for a single completed turn, per PRD §3.7 (zero-guess gate
 * added by owner decision, audit 2026-06-11).
 *
 *   drawer_pts = n >= 1 ? round(mult * (250 + 250 * n)) : 0
 *
 * Example (PRD): 5-player room (4 audience), all 4 guess, medium tier →
 * round(1.3 * (250 + 1000)) = 1625. Nobody guesses → 0 (the base used to
 * pay out unconditionally; gated so unguessable scribbles score nothing).
 *
 * @param numCorrectGuessers  Count of audience members who guessed
 *                            correctly. Clamped: <0 → 0; non-integer
 *                            floored.
 * @param tier                Word tier (easy/medium/hard).
 *
 * @returns Non-negative integer. Uses `Math.round`.
 */
export function drawerPointsForTurn(numCorrectGuessers: number, tier: WordTier): number {
  const n = Number.isFinite(numCorrectGuessers) ? Math.max(0, Math.floor(numCorrectGuessers)) : 0;
  if (n === 0) return 0;
  const raw = DIFFICULTY_MULTIPLIER[tier] * (250 + 250 * n);
  return Math.max(0, Math.round(raw));
}
